import React, { useState, useMemo } from 'react';
import { Helmet } from 'react-helmet';
import {
  Button,
  CssBaseline,
  TextField,
  Box,
  Typography,
  Fade,
  InputAdornment,
  IconButton,
  CircularProgress,
  Collapse,
  Alert,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import PersonIcon from '@mui/icons-material/Person';
import LockIcon from '@mui/icons-material/Lock';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import MusicNoteIcon from '@mui/icons-material/MusicNote';
import AudiotrackIcon from '@mui/icons-material/Audiotrack';
import QueueMusicIcon from '@mui/icons-material/QueueMusic';
import LinkIcon from '@mui/icons-material/Link';
import InsertLinkIcon from '@mui/icons-material/InsertLink';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import login1 from '../../assets/img/login1.mp4';
import login2 from '../../assets/img/login2.mp4';
import login3 from '../../assets/img/login3.mp4';
import login4 from '../../assets/img/login4.mp4';
import { useHistory } from 'react-router-dom';
import logo from '../../assets/img/logo.png';
import { saveObject, toastError } from '../../utils';
import { loginAction } from './actions';
import LoginFormValidate from './LoginFormValidate';

const loginVideos = [login1, login2, login3, login4];
const randomVideo = loginVideos[Math.floor(Math.random() * loginVideos.length)];

// Full-screen background
const PageWrapper = styled(Box)({
  position: 'relative',
  width: '100vw',
  height: '100vh',
  overflow: 'hidden',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  backgroundColor: '#0A0E1A',
  '&::before': {
    content: '""',
    position: 'absolute',
    inset: 0,
    background: `linear-gradient(135deg,
      rgba(10, 14, 26, 0.82) 0%,
      rgba(26, 31, 53, 0.70) 50%,
      rgba(10, 14, 26, 0.85) 100%)`,
    zIndex: 2,
  },
  '&::after': {
    content: '""',
    position: 'absolute',
    inset: 0,
    background: `radial-gradient(circle at 20% 50%, rgba(156,39,176,0.18) 0%, transparent 50%),
                 radial-gradient(circle at 80% 30%, rgba(233,30,99,0.12) 0%, transparent 40%),
                 radial-gradient(circle at 60% 80%, rgba(0,188,212,0.10) 0%, transparent 40%)`,
    zIndex: 3,
    animation: 'pulse 8s ease-in-out infinite',
  },
  '@keyframes pulse': {
    '0%, 100%': { opacity: 0.5 },
    '50%': { opacity: 0.9 },
  },
});

const BgVideo = styled('video')({
  position: 'absolute',
  top: '50%',
  left: '50%',
  minWidth: '100%',
  minHeight: '100%',
  width: 'auto',
  height: 'auto',
  transform: 'translate(-50%, -50%)',
  objectFit: 'cover',
  zIndex: 1,
});

const FloatingIcon = styled('span')(({ delay, duration, left, size, iconcolor }) => ({
  position: 'absolute',
  zIndex: 3,
  bottom: '-60px',
  left: left,
  fontSize: size || 24,
  color: iconcolor || 'rgba(156,39,176,0.5)',
  pointerEvents: 'none',
  animation: `floatUp ${duration}s ${delay}s ease-in infinite`,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  '@keyframes floatUp': {
    '0%': {
      transform: 'translateY(0) rotate(0deg)',
      opacity: 0,
    },
    '10%': {
      opacity: 1,
    },
    '90%': {
      opacity: 0.6,
    },
    '100%': {
      transform: 'translateY(-110vh) rotate(720deg)',
      opacity: 0,
    },
  },
}));

// Center content wrapper
const CenterContent = styled(Box)({
  position: 'relative',
  zIndex: 4,
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: 24,
  width: '100%',
  maxWidth: 400,
  padding: '0 24px',
});

const LoginCard = styled(Box)(({ theme }) => ({
  position: 'relative',
  background: 'rgba(20, 24, 40, 0.55)',
  backdropFilter: 'blur(24px) saturate(180%)',
  borderRadius: 20,
  padding: theme.spacing(3.5, 3.5),
  boxShadow: `
    0 8px 32px rgba(0,0,0,0.4),
    0 0 0 1px rgba(255,255,255,0.06) inset,
    0 0 60px rgba(156,39,176,0.15)
  `,
  border: '1px solid rgba(255,255,255,0.08)',
  width: '100%',
  [theme.breakpoints.down('sm')]: {
    padding: theme.spacing(4, 3),
  },
}));

const StyledTextField = styled(TextField)({
  '& .MuiOutlinedInput-root': {
    backgroundColor: 'rgba(10, 14, 26, 0.5)',
    color: '#E2E8F0',
    transition: 'all 0.3s ease',
    '& fieldset': {
      borderColor: 'rgba(255,255,255,0.12)',
      borderWidth: 1,
    },
    '&:hover': {
      backgroundColor: 'rgba(10, 14, 26, 0.7)',
      '& fieldset': {
        borderColor: 'rgba(156,39,176,0.6)',
      },
    },
    '&.Mui-focused': {
      backgroundColor: 'rgba(10, 14, 26, 0.6)',
      '& fieldset': {
        borderColor: '#9C27B0',
        borderWidth: 2,
      },
      boxShadow: '0 0 0 4px rgba(156,39,176,0.15)',
    },
  },
  '& .MuiInputLabel-root': {
    color: 'rgba(226,232,240,0.7)',
    '&.Mui-focused': {
      color: '#9C27B0',
    },
  },
  '& .MuiOutlinedInput-input': {
    color: '#E2E8F0',
  },
  '& .MuiFormHelperText-root': {
    color: 'rgba(255,107,107,0.95)',
    marginLeft: 0,
  },
});

const GradientButton = styled(Button)({
  background: 'linear-gradient(135deg, #9C27B0 0%, #E91E63 50%, #00BCD4 100%)',
  color: '#fff',
  padding: '12px 32px',
  fontSize: '1rem',
  fontWeight: 600,
  textTransform: 'none',
  borderRadius: 12,
  boxShadow: '0 8px 24px rgba(156,39,176,0.45)',
  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  '&:hover': {
    background: 'linear-gradient(135deg, #7B1FA2 0%, #C2185B 50%, #0097A7 100%)',
    transform: 'translateY(-2px)',
    boxShadow: '0 12px 32px rgba(156,39,176,0.6), 0 0 20px rgba(233,30,99,0.3)',
  },
  '&:active': {
    transform: 'translateY(0)',
  },
  '&.Mui-disabled': {
    background: 'rgba(100,116,139,0.3)',
    color: 'rgba(255,255,255,0.5)',
  },
});

export default function Login() {
  const history = useHistory();
  const [user, setUser] = useState({ username: '', password: '' });
  const [errors, setErrors] = useState({});
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [loginError, setLoginError] = useState('');

  const floatingItems = useMemo(() => {
    const icons = [
      { type: 'text', content: '\u266A' },
      { type: 'text', content: '\u266B' },
      { type: 'text', content: '\u266C' },
      { type: 'component', Component: MusicNoteIcon },
      { type: 'component', Component: AudiotrackIcon },
      { type: 'component', Component: QueueMusicIcon },
      { type: 'component', Component: LinkIcon },
      { type: 'component', Component: InsertLinkIcon },
      { type: 'component', Component: AttachFileIcon },
      { type: 'text', content: '\u266A' },
      { type: 'component', Component: MusicNoteIcon },
      { type: 'component', Component: LinkIcon },
      { type: 'text', content: '\u266B' },
      { type: 'component', Component: AudiotrackIcon },
      { type: 'component', Component: InsertLinkIcon },
      { type: 'text', content: '\u266C' },
      { type: 'component', Component: QueueMusicIcon },
      { type: 'component', Component: AttachFileIcon },
    ];
    const colors = [
      'rgba(156,39,176,0.5)',   // purple
      'rgba(233,30,99,0.5)',    // pink
      'rgba(0,188,212,0.5)',    // cyan
      'rgba(255,152,0,0.45)',   // orange
      'rgba(76,175,80,0.45)',   // green
      'rgba(63,81,181,0.5)',    // indigo
      'rgba(255,87,34,0.45)',   // deep orange
      'rgba(0,150,136,0.45)',   // teal
    ];
    return icons.map((icon, i) => ({
      ...icon,
      id: i,
      left: `${Math.random() * 95}%`,
      delay: Math.random() * 12,
      duration: 10 + Math.random() * 10,
      size: 16 + Math.random() * 20,
      color: colors[Math.floor(Math.random() * colors.length)],
    }));
  }, []);

  const onChangeHandler = ({ target: { value = '', name = '' } }) => {
    setUser({ ...user, [name]: value });
    setErrors({ ...errors, [name]: '' });
    if (loginError) setLoginError('');
  };

  const onSubmit = (e) => {
    e.preventDefault();
    const { errors, isValid } = LoginFormValidate(user);
    setErrors(errors);
    setLoginError('');
    if (isValid) {
      setLoading(true);
      loginAction(user)
        .then((res) => {
          saveObject('auth', res.body.token);
          history.push('/');
        })
        .catch((err) => {
          const message = err.message || err.response?.data?.message || 'Unable to connect. Please try again.';
          setLoginError(message);
          toastError(message);
        })
        .finally(() => {
          setLoading(false);
        });
    }
  };

  return (
    <>
      <Helmet>
        <title>Login - MusiLinks</title>
      </Helmet>
      <CssBaseline />
      <PageWrapper>
        <BgVideo autoPlay muted loop playsInline src={randomVideo} />

        {floatingItems.map((item) => (
          <FloatingIcon
            key={item.id}
            delay={item.delay}
            duration={item.duration}
            left={item.left}
            size={item.size}
            iconcolor={item.color}
          >
            {item.type === 'text' ? (
              item.content
            ) : (
              <item.Component sx={{ fontSize: 'inherit', color: 'inherit' }} />
            )}
          </FloatingIcon>
        ))}

        <CenterContent>
          <Fade in={true} timeout={800}>
            <LoginCard>
              {/* Brand */}
              <Box sx={{ textAlign: 'center', mb: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1, mb: 0.5 }}>
                  <Box
                    component="img"
                    src={logo}
                    alt="MusiLinks"
                    sx={{
                      width: 36,
                      height: 36,
                      objectFit: 'contain',
                      filter: 'drop-shadow(0 0 12px rgba(156,39,176,0.6))',
                    }}
                  />
                  <Typography
                    variant="h5"
                    sx={{
                      fontWeight: 800,
                      background: 'linear-gradient(135deg, #fff 0%, #9C27B0 40%, #E91E63 70%, #00BCD4 100%)',
                      WebkitBackgroundClip: 'text',
                      WebkitTextFillColor: 'transparent',
                      letterSpacing: '-0.02em',
                    }}
                  >
                    MusiLinks
                  </Typography>
                </Box>
                <Typography
                  variant="caption"
                  sx={{
                    color: '#FFFFFF',
                    letterSpacing: '0.15em',
                    textTransform: 'uppercase',
                    fontSize: '0.65rem',
                  }}
                >
                  A Fronic Media Product
                </Typography>
              </Box>

              {/* Login Form */}
              <Box component="form" onSubmit={onSubmit} noValidate>
                <StyledTextField
                  fullWidth
                  type="text"
                  name="username"
                  label="Username"
                  value={user.username}
                  onChange={onChangeHandler}
                  error={!!errors.username}
                  helperText={errors.username}
                  autoComplete="username"
                  autoFocus
                  sx={{ mb: 2 }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <PersonIcon sx={{ color: 'rgba(156,39,176,0.8)', fontSize: 20 }} />
                      </InputAdornment>
                    ),
                  }}
                />

                <StyledTextField
                  fullWidth
                  type={showPassword ? 'text' : 'password'}
                  name="password"
                  label="Password"
                  value={user.password}
                  onChange={onChangeHandler}
                  error={!!errors.password}
                  helperText={errors.password}
                  autoComplete="current-password"
                  sx={{ mb: 2.5 }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <LockIcon sx={{ color: 'rgba(156,39,176,0.8)', fontSize: 20 }} />
                      </InputAdornment>
                    ),
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() => setShowPassword(!showPassword)}
                          edge="end"
                          sx={{
                            color: 'rgba(203,213,225,0.6)',
                            '&:hover': { color: 'rgba(156,39,176,0.9)' },
                          }}
                        >
                          {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />

                <Collapse in={!!loginError}>
                  <Alert
                    severity="error"
                    onClose={() => setLoginError('')}
                    sx={{
                      mb: 2,
                      backgroundColor: 'rgba(255,107,107,0.1)',
                      border: '1px solid rgba(255,107,107,0.25)',
                      color: '#FF6B6B',
                      borderRadius: 2,
                      '& .MuiAlert-icon': { color: '#FF6B6B' },
                      '& .MuiAlert-action .MuiIconButton-root': { color: 'rgba(255,107,107,0.7)' },
                    }}
                  >
                    {loginError}
                  </Alert>
                </Collapse>

                <GradientButton type="submit" fullWidth disabled={loading}>
                  {loading ? <CircularProgress size={24} sx={{ color: '#fff' }} /> : 'Sign In'}
                </GradientButton>
              </Box>

              {/* Create Account */}
              <Box sx={{ mt: 2.5, textAlign: 'center' }}>
                <Typography variant="caption" sx={{ color: 'rgba(148,163,184,0.7)', fontSize: '0.75rem' }}>
                  To create an account, please reach out to us at{' '}
                  <Box
                    component="a"
                    href="mailto:support@fronicmedia.com"
                    sx={{
                      color: '#9C27B0',
                      textDecoration: 'none',
                      '&:hover': { textDecoration: 'underline' },
                    }}
                  >
                    support@fronicmedia.com
                  </Box>
                </Typography>
              </Box>

              {/* Footer */}
              <Box sx={{ mt: 2, textAlign: 'center' }}>
                <Typography variant="caption" sx={{ color: 'rgba(148,163,184,0.5)', fontSize: '0.72rem' }}>
                  &copy; {new Date().getFullYear()} MusiLinks. All rights reserved.
                </Typography>
              </Box>
            </LoginCard>
          </Fade>
        </CenterContent>
      </PageWrapper>
    </>
  );
}
